import React, { useEffect, useId, useMemo, useState } from 'react';
import { Button, Spinner } from '@/components/ui';
import { usePermissions } from '../../../hooks/usePermissions';
import formAttachmentService, { type FormAttachment } from '../../../services/formAttachmentService';
import {
  addHiredEquipmentSubmissionPdf,
  removeHiredEquipmentSubmissionPdf,
} from '../../../services/hiredEquipmentAttachmentService';
import { HE_AUTO_APPROVE_PERMISSION } from '../../forms/utils/submissionPermissions';
import FormPictures, { isImageAttachment, isPdfAttachment } from '../../forms/components/FormPictures';
import type { HiredEquipmentGridRow } from '../types';
import { confirmAction } from '@/components/ui';

export type HiredEquipmentTaskPictureLine = Pick<
  HiredEquipmentGridRow,
  'clientId' | 'taskGroupId' | 'taskLabel' | 'resourceLabel'
>;

interface HiredEquipmentTaskPicturesProps {
  formId: string;
  rows: HiredEquipmentTaskPictureLine[];
}

function lineHeading(row: HiredEquipmentTaskPictureLine, lineNumber: number): string {
  const task = row.taskLabel?.trim() || 'Task';
  const resource = row.resourceLabel?.trim();
  return resource ? `Line ${lineNumber} – ${task} – ${resource}` : `Line ${lineNumber} – ${task}`;
}

function downloadUrl(formId: string, attachmentId: number): string {
  const baseUrl = (process.env.REACT_APP_API_URL || 'http://localhost:5000').replace(/\/api\/?$/, '');
  return `${baseUrl}/api/forms/${formId}/attachments/${attachmentId}/download`;
}

function canManageSubmissionPdfs(hasPermission: (key: string) => boolean): boolean {
  return (
    hasPermission('submission:he_manage') ||
    hasPermission('submission:hired_equipment_approve') ||
    hasPermission(HE_AUTO_APPROVE_PERMISSION)
  );
}

const HiredEquipmentTaskPictures: React.FC<HiredEquipmentTaskPicturesProps> = ({ formId, rows }) => {
  const { hasPermission } = usePermissions();
  const canManagePdfs = canManageSubmissionPdfs(hasPermission);
  const [attachmentsByTask, setAttachmentsByTask] = useState<Record<string, FormAttachment[]>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lineError, setLineError] = useState<Record<string, string | null>>({});
  const [busyLine, setBusyLine] = useState<string | null>(null);

  const lines = useMemo(
    () =>
      rows
        .map((row, index) => ({ row, lineNumber: index + 1, lineIndex: index }))
        .filter((entry): entry is { row: HiredEquipmentTaskPictureLine & { taskGroupId: string }; lineNumber: number; lineIndex: number } =>
          Boolean(entry.row.taskGroupId),
        ),
    [rows],
  );

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    void formAttachmentService
      .getFormAttachmentsByTask(formId)
      .then((grouped) => {
        if (!cancelled) setAttachmentsByTask(grouped);
      })
      .catch(() => {
        if (!cancelled) setError('Failed to load pictures');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [formId]);

  const reload = async () => {
    const grouped = await formAttachmentService.getFormAttachmentsByTask(formId);
    setAttachmentsByTask(grouped);
  };

  const handleAdd = async (clientId: string, lineIndex: number, file: File | undefined) => {
    if (!file) return;
    const isPdf = file.type === 'application/pdf' || /\.pdf$/i.test(file.name);
    if (!isPdf) {
      setLineError((prev) => ({ ...prev, [clientId]: 'PDFs only' }));
      return;
    }
    setLineError((prev) => ({ ...prev, [clientId]: null }));
    setBusyLine(clientId);
    try {
      await addHiredEquipmentSubmissionPdf(formId, lineIndex, file);
      await reload();
    } catch (err) {
      setLineError((prev) => ({
        ...prev,
        [clientId]: err instanceof Error ? err.message : 'Failed to add PDF',
      }));
    } finally {
      setBusyLine(null);
    }
  };

  const handleRemove = async (clientId: string, attachment: FormAttachment) => {
    if (!(await confirmAction({ title: `Remove "${attachment.filename}"?`, body: 'This cannot be undone.', confirmLabel: 'Remove', destructive: true }))) return;
    setLineError((prev) => ({ ...prev, [clientId]: null }));
    setBusyLine(clientId);
    try {
      await removeHiredEquipmentSubmissionPdf(formId, attachment.id);
      await reload();
    } catch (err) {
      setLineError((prev) => ({
        ...prev,
        [clientId]: err instanceof Error ? err.message : 'Failed to remove PDF',
      }));
    } finally {
      setBusyLine(null);
    }
  };

  if (lines.length === 0) return null;

  if (loading) {
    return (
      <div className="tw-mt-4 tw-rounded-lg tw-border tw-border-line tw-bg-surface tw-p-4">
        <p className="tw-mb-0 tw-text-sm tw-text-content-muted">Loading pictures…</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="tw-mt-4 tw-rounded-lg tw-border tw-border-danger/30 tw-bg-danger/10 tw-p-4">
        <p className="tw-mb-0 tw-text-sm tw-text-danger">{error}</p>
      </div>
    );
  }

  const sections = lines
    .map(({ row, lineNumber, lineIndex }) => {
      const attachments = attachmentsByTask[`task-${row.taskGroupId}`] ?? [];
      if (!canManagePdfs && attachments.length === 0) return null;
      return (
        <LineFiles
          key={row.clientId}
          formId={formId}
          row={row}
          lineNumber={lineNumber}
          attachments={attachments}
          canManagePdfs={canManagePdfs}
          busy={busyLine === row.clientId}
          error={lineError[row.clientId] ?? null}
          onAdd={(file) => void handleAdd(row.clientId, lineIndex, file)}
          onRemove={(attachment) => void handleRemove(row.clientId, attachment)}
        />
      );
    })
    .filter(Boolean);

  if (sections.length === 0) return null;

  return (
    <div className="tw-mt-4">
      <h3 className="tw-mb-1 tw-text-base tw-font-semibold tw-text-content">Pictures by line</h3>
      {canManagePdfs && (
        <p className="tw-mb-2 tw-text-sm tw-text-content-muted">
          PDF tickets and invoices can be added or removed after this form is submitted, including after approval.
        </p>
      )}
      {sections}
    </div>
  );
};

interface LineFilesProps {
  formId: string;
  row: HiredEquipmentTaskPictureLine;
  lineNumber: number;
  attachments: FormAttachment[];
  canManagePdfs: boolean;
  busy: boolean;
  error: string | null;
  onAdd: (file: File | undefined) => void;
  onRemove: (attachment: FormAttachment) => void;
}

const LineFiles: React.FC<LineFilesProps> = ({
  formId,
  row,
  lineNumber,
  attachments,
  canManagePdfs,
  busy,
  error,
  onAdd,
  onRemove,
}) => {
  const inputId = useId();
  const heading = lineHeading(row, lineNumber);

  if (!canManagePdfs) {
    return (
      <div className="tw-mt-3">
        <FormPictures
          formId={formId}
          attachmentsOverride={attachments}
          showOnlyWhenExpanded={false}
          isExpanded={true}
          hideWhenEmpty={true}
          title={heading}
        />
      </div>
    );
  }

  const pdfs = attachments.filter((attachment) => isPdfAttachment(attachment));
  const images = attachments.filter((attachment) => isImageAttachment(attachment));

  return (
    <div className="tw-mt-3 tw-rounded-lg tw-border tw-border-line tw-bg-surface tw-p-3">
      <h4 className="tw-mb-2 tw-text-sm tw-font-semibold tw-text-content">{heading}</h4>
      {pdfs.length > 0 && (
        <ul className="tw-mb-2 tw-list-none tw-space-y-2 tw-p-0">
          {pdfs.map((attachment) => {
            const href = downloadUrl(formId, attachment.id);
            return (
              <li key={attachment.id} className="tw-flex tw-flex-wrap tw-items-center tw-gap-2">
                <a
                  className="tw-inline-flex tw-min-h-11 tw-min-w-0 tw-items-center tw-gap-2 tw-text-sm tw-font-medium tw-text-primary hover:tw-underline"
                  href={href}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={`Open ${attachment.filename}`}
                >
                  <i className="bi bi-file-earmark-pdf" aria-hidden="true" />
                  <span className="tw-truncate">{attachment.filename}</span>
                </a>
                <a
                  className="tw-inline-flex tw-min-h-11 tw-items-center tw-text-sm tw-text-content-muted hover:tw-text-content"
                  href={href}
                  download={attachment.filename}
                  aria-label={`Download ${attachment.filename}`}
                >
                  <i className="bi bi-download tw-mr-1" aria-hidden="true" />
                  Download
                </a>
                <Button
                  type="button"
                  variant="outline-danger"
                  size="sm"
                  className="tw-min-h-11"
                  disabled={busy}
                  aria-label={`Remove ${attachment.filename}`}
                  onClick={() => onRemove(attachment)}
                >
                  Remove
                </Button>
              </li>
            );
          })}
        </ul>
      )}
      {images.length > 0 && (
        <FormPictures
          formId={formId}
          attachmentsOverride={images}
          showOnlyWhenExpanded={false}
          isExpanded={true}
          hideWhenEmpty={true}
          title="Pictures"
        />
      )}
      <div className="tw-mt-2">
        <input
          id={inputId}
          type="file"
          accept="application/pdf,.pdf"
          className="tw-sr-only"
          aria-label={`Add PDF, line ${lineNumber}`}
          disabled={busy}
          onChange={(event) => {
            const file = event.target.files?.[0];
            event.target.value = '';
            onAdd(file);
          }}
        />
        {busy ? (
          <span className="tw-inline-flex tw-min-h-11 tw-items-center tw-gap-2 tw-text-sm tw-text-content-muted">
            <Spinner animation="border" size="sm" aria-hidden="true" />
            Saving…
          </span>
        ) : (
          <label
            htmlFor={inputId}
            className="tw-mb-0 tw-inline-flex tw-min-h-11 tw-cursor-pointer tw-items-center tw-gap-2 tw-text-sm tw-font-medium tw-text-primary hover:tw-underline"
          >
            <i className="bi bi-file-earmark-plus" aria-hidden="true" />
            Add PDF
          </label>
        )}
      </div>
      {error ? (
        <p className="tw-mb-0 tw-mt-1 tw-text-sm tw-text-danger" role="alert">
          {error}
        </p>
      ) : null}
    </div>
  );
};

export default HiredEquipmentTaskPictures;
